import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plug, RefreshCw, Gauge as GaugeIcon, Search, CheckCircle2, XCircle } from "lucide-react";

const scoreCls = (v) => (v == null ? "text-zinc-400" : v >= 90 ? "text-emerald-600" : v >= 70 ? "text-amber-600" : "text-red-600");

function ConfigDialog({ open, onOpenChange, status, onSaved }) {
  const [psiKey, setPsiKey] = useState("");
  const [gscJson, setGscJson] = useState("");
  const [gscProp, setGscProp] = useState(status?.gsc_property || "");
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    const body = {};
    if (psiKey.trim()) body.psi_api_key = psiKey.trim();
    if (gscJson.trim()) body.gsc_json = gscJson.trim();
    if (gscProp.trim()) body.gsc_property = gscProp.trim();
    if (!Object.keys(body).length) return toast.error("Nothing to save");
    setBusy(true);
    try {
      await api.put("/admin/health/integrations", body);
      toast.success("Credentials saved");
      setPsiKey(""); setGscJson("");
      onSaved();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setTestResult(null);
    try {
      const r = await api.post("/admin/health/integrations/test");
      setTestResult(r.data);
    } catch { toast.error("Test failed to run"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="google-config-dialog">
        <DialogHeader>
          <DialogTitle>Google credentials</DialogTitle>
          <DialogDescription>Stored securely in the backend database — never exposed to visitors.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-600">PageSpeed Insights API key {status?.psi_configured && <span className="text-emerald-600">(saved: {status.psi_key_masked})</span>}</label>
            <Input value={psiKey} onChange={(e) => setPsiKey(e.target.value)} placeholder="AIza…" className="mt-1" data-testid="psi-key-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Search Console service account JSON {status?.gsc_email && <span className="text-emerald-600">(saved: {status.gsc_email})</span>}</label>
            <Textarea value={gscJson} onChange={(e) => setGscJson(e.target.value)} rows={4} placeholder='{"type":"service_account","client_email":"...","private_key":"..."}' className="mt-1 font-mono text-xs" data-testid="gsc-json-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Search Console property</label>
            <Input value={gscProp} onChange={(e) => setGscProp(e.target.value)} placeholder="sc-domain:happyretreats.in  or  https://happyretreats.in/" className="mt-1" data-testid="gsc-property-input" />
            <p className="text-[11px] text-zinc-400 mt-1">Use sc-domain:happyretreats.in for a Domain property, or the exact URL prefix.</p>
          </div>
          {testResult && (
            <div className="text-xs space-y-1" data-testid="test-results">
              <div className={`flex items-start gap-1.5 ${testResult.psi?.ok ? "text-emerald-700" : "text-red-600"}`}>
                {testResult.psi?.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                PageSpeed: {testResult.psi?.ok ? "connected" : testResult.psi?.error}
              </div>
              <div className={`flex items-start gap-1.5 ${testResult.gsc?.ok ? "text-emerald-700" : "text-red-600"}`}>
                {testResult.gsc?.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                Search Console: {testResult.gsc?.ok ? `connected (${(testResult.gsc.properties || []).join(", ")})` : testResult.gsc?.error}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy} data-testid="save-google-creds">Save</Button>
            <Button variant="outline" onClick={test} disabled={busy} data-testid="test-google-creds">Test connection</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GoogleIntegrations() {
  const [status, setStatus] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [psi, setPsi] = useState([]);
  const [psiRunning, setPsiRunning] = useState(false);
  const [gsc, setGsc] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const s = await api.get("/admin/health/integrations");
    setStatus(s.data);
    if (s.data.psi_configured) api.get("/admin/health/psi/latest").then((r) => setPsi(r.data));
    if (s.data.gsc_configured) api.get("/admin/health/gsc/summary").then((r) => setGsc(r.data));
  }, []);

  useEffect(() => { load().catch(() => {}); return () => clearInterval(pollRef.current); }, [load]);

  const runPsi = async () => {
    setPsiRunning(true);
    try {
      const r = await api.post("/admin/health/psi/run");
      const id = r.data.run_id;
      toast.info("PageSpeed audit started — this takes 1–3 minutes (Google analyses each page)");
      pollRef.current = setInterval(async () => {
        const s = await api.get(`/admin/health/psi/run/${id}`);
        if (s.data.status !== "running") {
          clearInterval(pollRef.current);
          setPsiRunning(false);
          if (s.data.status === "done") { toast.success("PageSpeed audit complete"); load(); }
          else toast.error(`Audit failed: ${s.data.error || "unknown"}`);
        }
      }, 4000);
    } catch (e) {
      setPsiRunning(false);
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not start audit");
    }
  };

  const syncGsc = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/health/gsc/sync");
      toast.success(`Synced — ${r.data.totals.clicks} clicks / ${r.data.totals.impressions} impressions (28d), ${r.data.inspected} pages inspected`);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Sync failed");
    } finally { setSyncing(false); }
  };

  if (!status) return null;

  return (
    <div className="bg-white border rounded-xl mt-6" data-testid="google-integrations">
      <ConfigDialog open={configOpen} onOpenChange={setConfigOpen} status={status} onSaved={() => { setConfigOpen(false); load(); }} />
      <div className="flex flex-wrap items-center gap-3 p-4 border-b">
        <Plug className="w-5 h-5 text-zinc-500" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-medium">Google Integrations</div>
          <div className="text-xs text-zinc-500">PageSpeed Insights + Search Console. Data refreshes daily at 08:00 IST once connected.</div>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded-full ${status.psi_configured ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`} data-testid="psi-status-chip">
          PageSpeed {status.psi_configured ? "connected" : "not set"}
        </span>
        <span className={`text-[10px] px-2 py-1 rounded-full ${status.gsc_configured ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`} data-testid="gsc-status-chip">
          Search Console {status.gsc_configured ? "connected" : "not set"}
        </span>
        <Button size="sm" variant="outline" onClick={() => setConfigOpen(true)} data-testid="configure-google-btn">Configure</Button>
      </div>

      {status.psi_configured && (
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-medium text-zinc-600 flex items-center gap-1.5"><GaugeIcon className="w-3.5 h-3.5" /> PageSpeed audits (Lighthouse)</div>
            <Button size="sm" variant="outline" onClick={runPsi} disabled={psiRunning} data-testid="run-psi-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${psiRunning ? "animate-spin" : ""}`} /> {psiRunning ? "Auditing…" : "Run PageSpeed audit"}
            </Button>
          </div>
          {psi.length === 0 ? <p className="text-xs text-zinc-400">No audits yet — run your first audit.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="psi-results-table">
                <thead className="text-zinc-500">
                  <tr className="text-left"><th className="py-1.5 pr-3">Page</th><th className="py-1.5 pr-3">Device</th><th className="py-1.5 pr-3">Perf</th><th className="py-1.5 pr-3">SEO</th><th className="py-1.5 pr-3">A11y</th><th className="py-1.5 pr-3">Best</th><th className="py-1.5 pr-3">LCP</th><th className="py-1.5 pr-3">CLS</th><th className="py-1.5">Fetched</th></tr>
                </thead>
                <tbody>
                  {psi.map((r) => (
                    <tr key={`${r.path}-${r.strategy}`} className="border-t border-zinc-100">
                      <td className="py-1.5 pr-3 font-medium">{r.path}</td>
                      <td className="py-1.5 pr-3 capitalize">{r.strategy}</td>
                      {r.error ? <td colSpan={6} className="py-1.5 text-red-500">{r.error}</td> : <>
                        <td className={`py-1.5 pr-3 font-semibold ${scoreCls(r.scores?.performance)}`}>{r.scores?.performance ?? "—"}</td>
                        <td className={`py-1.5 pr-3 ${scoreCls(r.scores?.seo)}`}>{r.scores?.seo ?? "—"}</td>
                        <td className={`py-1.5 pr-3 ${scoreCls(r.scores?.accessibility)}`}>{r.scores?.accessibility ?? "—"}</td>
                        <td className={`py-1.5 pr-3 ${scoreCls(r.scores?.best_practices)}`}>{r.scores?.best_practices ?? "—"}</td>
                        <td className="py-1.5 pr-3">{r.lab?.lcp_ms ? `${(r.lab.lcp_ms / 1000).toFixed(1)}s` : "—"}</td>
                        <td className="py-1.5 pr-3">{r.lab?.cls != null ? r.lab.cls.toFixed(3) : "—"}</td>
                      </>}
                      <td className="py-1.5 text-zinc-400">{r.fetched_at ? new Date(r.fetched_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {status.gsc_configured && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-medium text-zinc-600 flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Search Console — last 28 days</div>
            <Button size="sm" variant="outline" onClick={syncGsc} disabled={syncing} data-testid="sync-gsc-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync Search Console"}
            </Button>
          </div>
          {!gsc?.synced ? <p className="text-xs text-zinc-400">Not synced yet — run your first sync.</p> : (
            <div className="space-y-4" data-testid="gsc-summary">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[["Clicks", gsc.totals.clicks], ["Impressions", gsc.totals.impressions], ["CTR", `${gsc.totals.ctr}%`], ["Avg position", gsc.totals.position]].map(([l, v]) => (
                  <div key={l} className="border rounded-lg p-3">
                    <div className="text-lg font-semibold">{v ?? "—"}</div>
                    <div className="text-[11px] text-zinc-500">{l}</div>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] font-medium text-zinc-500 mb-1.5">Top queries</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {(gsc.top_queries || []).slice(0, 8).map((q) => (
                        <tr key={q.query} className="border-t border-zinc-100">
                          <td className="py-1 pr-2 truncate max-w-[180px]">{q.query}</td>
                          <td className="py-1 pr-2 text-right">{q.clicks} cl</td>
                          <td className="py-1 pr-2 text-right text-zinc-400">{q.impressions} imp</td>
                          <td className="py-1 text-right text-zinc-400">#{q.position}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="text-[11px] font-medium text-zinc-500 mb-1.5">Index status (key pages)</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {(gsc.inspections || []).slice(0, 8).map((i) => (
                        <tr key={i.path} className="border-t border-zinc-100">
                          <td className="py-1 pr-2 truncate max-w-[180px]">{i.path}</td>
                          <td className="py-1 text-right">
                            {i.error ? <span className="text-red-500">error</span> :
                              i.indexed ? <span className="text-emerald-600">indexed</span> :
                              <span className="text-amber-600">{i.coverage_state || i.verdict || "unknown"}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="text-[11px] text-zinc-400">Last synced {new Date(gsc.synced_at).toLocaleString()} · window {gsc.start} → {gsc.end}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
